import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { User, ShoppingBag, Settings, LogOut, Package, Clock, CheckCircle, MapPin, FileImage, Palette, FileText, Receipt } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { ordersAPI } from '../services/api';
import Header from './Header';
import Footer from './Footer';
import LoadingSpinner from './LoadingSpinner';
import AddressManagement from './AddressManagement';
import LabelUploadArea from './LabelUploadArea';
import MyLabels from './MyLabels';
import { useToast } from '../hooks/use-toast';
import CustomerQuotes from './Customer/CustomerQuotes';
import CustomerInvoices from './Customer/CustomerInvoices';

const Dashboard = () => {
  const { user, logout, isAuthenticated } = useAuth();
  const { toast } = useToast();
  const [orders, setOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(true);
  
  // Get tab from URL parameter or default to 'orders'
  const urlParams = new URLSearchParams(window.location.search);
  const tabFromUrl = urlParams.get('tab') || 'orders';
  const [activeTab, setActiveTab] = useState(tabFromUrl);

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Logout error:', error);
      toast({
        title: "Logout completato",
        description: "Sei stato disconnesso con successo"
      });
    }
  };

  useEffect(() => {
    const loadUserOrders = async () => {
      if (!isAuthenticated()) return;
      
      try {
        setLoadingOrders(true);
        const result = await ordersAPI.getMyOrders(1, 5); // Get last 5 orders
        if (result.success) {
          setOrders(result.orders);
        }
      } catch (error) {
        console.error('Error loading orders:', error);
        toast({
          title: "Errore",
          description: "Impossibile caricare gli ordini.",
          variant: "destructive"
        });
      } finally {
        setLoadingOrders(false);
      }
    };

    loadUserOrders();
  }, [isAuthenticated, toast]);

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending_payment':
        return <Clock className="w-4 h-4 text-yellow-600" />;
      case 'payment_confirmed':
      case 'in_production':
        return <Package className="w-4 h-4 text-blue-600" />;
      case 'shipped':
      case 'delivered':
        return <CheckCircle className="w-4 h-4 text-green-600" />;
      default:
        return <Package className="w-4 h-4 text-gray-600" />;
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'pending_payment':
        return 'In attesa pagamento';
      case 'payment_confirmed':
        return 'Pagamento confermato';
      case 'in_production':
        return 'In lavorazione';
      case 'ready_to_ship':
        return 'Pronto per spedizione';
      case 'shipped':
        return 'Spedito';
      case 'delivered':
        return 'Consegnato';
      default:
        return status;
    }
  };

  if (!isAuthenticated()) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
          <div className="text-center">
            <h1 className="text-3xl font-bold text-gray-900 mb-4">Accesso Richiesto</h1>
            <p className="text-lg text-gray-600 mb-8">
              Devi effettuare l'accesso per visualizzare la dashboard.
            </p>
            <Link
              to="/login"
              className="inline-flex items-center px-6 py-3 bg-purple-600 text-white font-semibold rounded-lg hover:bg-purple-700 transition-colors"
            >
              Accedi
            </Link>
          </div>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Welcome Section */}
        <div className="bg-white rounded-lg shadow-lg p-8 mb-8">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900 mb-2">
                Benvenuto, {user?.firstName}!
              </h1>
              <p className="text-lg text-gray-600">
                Gestisci il tuo account e i tuoi ordini da qui.
              </p>
            </div>
            <div className="flex items-center space-x-4">
              <div className="w-16 h-16 bg-purple-100 text-purple-600 rounded-full flex items-center justify-center">
                <User className="w-8 h-8" />
              </div>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Link
            to="/builder"
            className="bg-white p-6 rounded-lg shadow-lg hover:shadow-xl transition-shadow duration-300 group"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-purple-100 text-purple-600 rounded-lg flex items-center justify-center group-hover:bg-purple-600 group-hover:text-white transition-colors">
                <ShoppingBag className="w-6 h-6" />
              </div>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Crea Liquore</h3>
            <p className="text-gray-600 text-sm">Personalizza il tuo liquore unico</p>
          </Link>

          <Link
            to="/carrello"
            className="bg-white p-6 rounded-lg shadow-lg hover:shadow-xl transition-shadow duration-300 group"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-green-100 text-green-600 rounded-lg flex items-center justify-center group-hover:bg-green-600 group-hover:text-white transition-colors">
                <ShoppingBag className="w-6 h-6" />
              </div>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Carrello</h3>
            <p className="text-gray-600 text-sm">Visualizza i tuoi prodotti</p>
          </Link>

          {/* Tab Indirizzi rimossa come richiesto */}

          <button
            onClick={handleLogout}
            className="bg-white p-6 rounded-lg shadow-lg hover:shadow-xl transition-shadow duration-300 group text-left"
          >
            <div className="flex items-center justify-between mb-4">
              <div className="w-12 h-12 bg-red-100 text-red-600 rounded-lg flex items-center justify-center group-hover:bg-red-600 group-hover:text-white transition-colors">
                <LogOut className="w-6 h-6" />
              </div>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Logout</h3>
            <p className="text-gray-600 text-sm">Esci dal tuo account</p>
          </button>
        </div>

        {/* Tabs Navigation */}
        <div className="bg-white rounded-lg shadow-lg mb-8">
          <div className="border-b border-gray-200">
            <nav className="-mb-px flex space-x-8 px-8">
              <button
                onClick={() => setActiveTab('overview')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'overview'
                    ? 'border-purple-500 text-purple-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Dati personali
              </button>
              {/* Tab Indirizzi rimossa come richiesto */}
              <button
                onClick={() => setActiveTab('orders')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'orders'
                    ? 'border-purple-500 text-purple-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                Ordini
              </button>
              <button
                onClick={() => setActiveTab('labels')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'labels'
                    ? 'border-purple-500 text-purple-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <FileImage className="w-4 h-4 inline-block mr-2" />
                Area invio file
              </button>
              <button
                onClick={() => setActiveTab('mylabels')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'mylabels'
                    ? 'border-purple-500 text-purple-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Palette className="w-4 h-4 inline-block mr-2" />
                Le Mie Etichette
              </button>
              <button
                onClick={() => setActiveTab('quotes')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'quotes'
                    ? 'border-purple-500 text-purple-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <FileText className="w-4 h-4 inline-block mr-2" />
                Preventivi
              </button>
              <button
                onClick={() => setActiveTab('invoices')}
                className={`py-4 px-1 border-b-2 font-medium text-sm ${
                  activeTab === 'invoices'
                    ? 'border-purple-500 text-purple-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <Receipt className="w-4 h-4 inline-block mr-2" />
                Fatture e Ricevute
              </button>
            </nav>
          </div>

          {/* Tab Content */}
          <div className="p-8">
            {activeTab === 'overview' && (
              <div>
                <h2 className="text-2xl font-bold text-gray-900 mb-6">Informazioni Account</h2>
          
          <div className="grid md:grid-cols-2 gap-8">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Dati Personali</h3>
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Nome</label>
                  <p className="text-gray-900">{user?.firstName}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Cognome</label>
                  <p className="text-gray-900">{user?.lastName}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Email</label>
                  <p className="text-gray-900">{user?.email}</p>
                </div>
                {user?.phone && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700">Telefono</label>
                    <p className="text-gray-900">{user.phone}</p>
                  </div>
                )}
              </div>
            </div>

            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Stato Account</h3>
              <div className="space-y-3">
                <div>
                  <label className="block text-sm font-medium text-gray-700">Tipo Account</label>
                  <p className="text-gray-900 capitalize">{user?.userType || 'Private'}</p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Email Verificata</label>
                  <p className={`font-medium ${user?.emailVerified ? 'text-green-600' : 'text-red-600'}`}>
                    {user?.emailVerified ? 'Verificata' : 'Non Verificata'}
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">Data Registrazione</label>
                  <p className="text-gray-900">
                    {user?.createdAt ? new Date(user.createdAt).toLocaleDateString('it-IT') : 'N/A'}
                  </p>
                </div>
              </div>
            </div>
          </div>
              </div>
            )}

            {/* Sezione Indirizzi rimossa come richiesto */}

            {activeTab === 'orders' && (
              <div>
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl font-bold text-gray-900">I Tuoi Ordini</h2>
                </div>

                {loadingOrders ? (
                  <div className="flex justify-center py-8">
                    <LoadingSpinner message="Caricamento ordini..." />
                  </div>
                ) : orders.length === 0 ? (
                  <div className="text-center py-12">
                    <Package className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">Nessun ordine ancora</h3>
                    <p className="text-gray-600 mb-6">
                      Non hai ancora effettuato nessun ordine. Inizia a creare il tuo liquore personalizzato!
                    </p>
                    <Link
                      to="/builder"
                      className="inline-flex items-center px-6 py-3 bg-purple-600 text-white font-semibold rounded-lg hover:bg-purple-700 transition-colors"
                    >
                      <ShoppingBag className="w-5 h-5 mr-2" />
                      Crea il Tuo Liquore
                    </Link>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {orders.map((order) => (
                      <div key={order.id} className="border border-gray-200 rounded-lg p-6 hover:bg-gray-50 transition-colors">
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="flex items-center space-x-3 mb-2">
                              <span className="font-semibold text-gray-900">#{order.orderNumber}</span>
                              <div className="flex items-center space-x-1">
                                {getStatusIcon(order.status)}
                                <span className="text-sm text-gray-600">{getStatusText(order.status)}</span>
                              </div>
                            </div>
                            <p className="text-sm text-gray-600">
                              {new Date(order.createdAt).toLocaleDateString('it-IT', {
                                year: 'numeric',
                                month: 'long',
                                day: 'numeric'
                              })}
                            </p>
                            
                            {/* Tracking Number */}
                            {order.shippingInfo?.trackingNumber && (
                              <div className="mt-2 flex items-center space-x-2 text-xs">
                                <Package className="w-3 h-3 text-purple-600" />
                                <span className="text-gray-500">Tracking:</span>
                                <span className="font-mono font-semibold text-purple-600">{order.shippingInfo.trackingNumber}</span>
                              </div>
                            )}
                            
                            {/* Mostra dettagli prodotti nell'ordine */}
                            <div className="mt-3 space-y-2">
                              {order.items && order.items.slice(0, 2).map((item, index) => {
                                // Use CORRECT ORDER structure - not cart structure!
                                const liquorType = item.liquorType;
                                const selectedIngredients = item.selectedIngredients || [];
                                const bottleName = item.bottleType?.name || item.bottleType;
                                const labelText = item.labelText;
                                
                                const productName = item.name || (liquorType ? `${liquorType} Personalizzato` : 'Prodotto Personalizzato');
                                
                                return (
                                  <div key={index} className="text-sm text-gray-600 bg-gray-50 rounded p-2">
                                    <p className="font-medium">{productName}</p>
                                    {liquorType && (
                                      <p className="text-xs">Base: {liquorType}</p>
                                    )}
                                    {selectedIngredients.length > 0 && (
                                      <p className="text-xs">Botaniche: {selectedIngredients.map(ing => typeof ing === 'string' ? ing : ing.name || ing).join(', ')}</p>
                                    )}
                                    {bottleName && (
                                      <p className="text-xs">Bottiglia: {bottleName}</p>
                                    )}
                                    {labelText && (
                                      <p className="text-xs">Etichetta: "{labelText}"</p>
                                    )}
                                  </div>
                                );
                              })}
                              {order.items && order.items.length > 2 && (
                                <p className="text-xs text-gray-500">
                                  +{order.items.length - 2} altri prodotti
                                </p>
                              )}
                            </div>
                          </div>
                          
                          <div className="text-right">
                            <p className="text-lg font-bold text-purple-600">
                              €{order.totalAmount.toFixed(2)}
                            </p>
                            <Link
                              to={`/order-details/${order.orderNumber}`}
                              className="text-sm text-purple-600 hover:text-purple-700 font-medium"
                            >
                              Vedi dettagli
                            </Link>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
            
            {activeTab === 'labels' && (
              <LabelUploadArea />
            )}
            
            {activeTab === 'mylabels' && (
              <MyLabels />
            )}
            
            {activeTab === 'quotes' && (
              <CustomerQuotes />
            )}

            {activeTab === 'invoices' && (
              <CustomerInvoices />
            )}
          </div>
        </div>
      </div>
      
      <Footer />
    </div>
  );
};

export default Dashboard;